import { authHeader } from "@roman/shared/lib/auth/authHeader";
import romanApi from "@roman/shared/lib/net/romanApi";
import type { ChildPredictionMarketComment } from "@roman/shared/types/Comment";
import type { Meta } from "@roman/shared/types/Meta";
import { useCallback } from "react";
import useSWRInfinite from "swr/infinite";

type ChildCommentResponse = {
  comments: ChildPredictionMarketComment[];
  meta: Meta;
};

type UseGetChildCommentsInput = {
  predictionMarketId: string;
  commentId: string;
};

type UseGetChildCommentsOptions = {
  initialPage?: number;
  initialPerPage?: number;
  infiniteScroll?: boolean;
  loadMoreButton?: boolean; // New option for load more button mode
  shouldFetch?: boolean;
  sessionId?: number; // Session ID to create unique cache keys
};

const fetcher = async (url: string): Promise<ChildCommentResponse> => {
  const headers = authHeader.get(); // Get auth headers if available
  return romanApi.get<ChildCommentResponse>(url, undefined, headers ? { headers } : undefined);
};

export const useGetChildComments = (
  { predictionMarketId, commentId }: UseGetChildCommentsInput,
  onlyEntryAuthor = false,
  options: UseGetChildCommentsOptions = {},
): {
  data: ChildPredictionMarketComment[];
  size: number; // for infinite scroll
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<ChildCommentResponse[] | undefined>;
  loadMore: () => Promise<void>;
  canLoadMore: boolean;
} => {
  const { initialPerPage = 10, sessionId } = options;

  const getKey = (pageIndex: number, previousPageData: ChildCommentResponse[] | null) => {
    if (previousPageData && !previousPageData.length) {
      return null; // Reached the end
    }
    const page = pageIndex + 1;
    // Include sessionId in key to create unique cache entries for each component instance
    const sessionParam = sessionId ? `&session=${sessionId}` : "";
    return `prediction-market/${predictionMarketId}/comments/${commentId}/replies?page=${page}&per=${initialPerPage}&onlyEntryAuthor=${onlyEntryAuthor}${sessionParam}`; // SWR key
  };

  const { data, error, isLoading, mutate, size, setSize } = useSWRInfinite<ChildCommentResponse, Error>(
    getKey,
    fetcher,
    {
      // Optimize SWR behavior for better UX
      revalidateOnFocus: true,
      revalidateOnReconnect: true, // Revalidate when connection is restored
      dedupingInterval: 5000, // Dedupe requests within 5 seconds
      errorRetryCount: 3, // Retry failed requests up to 3 times
      errorRetryInterval: 1000, // Wait 1 second between retries
      parallel: true,
    },
  );

  // Load more handlers (works for both infinite scroll and load more button)
  const loadMore = useCallback(async () => {
    setSize((prev) => prev + 1);
  }, [setSize]);

  return {
    // Data
    data: data?.flatMap((page) => page.comments) ?? [],
    size, // for infinite scroll
    error,
    loading: isLoading,
    refetch: mutate,
    // Load more specific (works for both infinite scroll and load more button)
    loadMore,
    canLoadMore: data?.[data.length - 1]?.meta.next !== null,
  };
};
